import { ButtonPrimary } from '@/components/button'
import { ArrowDropdown, Close } from '@/components/icons'
import { duprCountries } from '@/constants/Countries'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { useInviteClubMember, useInvitePlayer } from '@/services'
import { Country, PayloadClubInviteMember } from '@/types'
import { createInvitePlayerValidationSchema } from '@/utils/schema/InvitePlayerValidationSchema'
import { Dialog, Transition } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Fragment, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { SelectCountryModal } from './SelectCountryModal'
import { LoadingInline } from '../loading'

type Props = {
  open: boolean
  onClose: () => void
  clubId?: string
}

type FormValues = {
  firstName?: string
  lastName?: string
  email: string
  phoneData: {
    phoneNumber: string
    isoCode: string
    dialCode: string
  }
}

export const InvitePlayerModal = ({ open, onClose, clubId }: Props) => {
  const { t } = useTranslation()

  const [selectedCountry, setSelectedCountry] = useState<Country>(duprCountries[0])
  const [isOpen, setIsOpen] = useState(false)
  const [inviting, setInviting] = useState(false);

  const { mutate: inviteClubMember } = useInviteClubMember()
  const { mutate: invitePlayer } = useInvitePlayer()

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    phoneData: {
      phoneNumber: '',
      isoCode: selectedCountry.isoCode,
      dialCode: selectedCountry.dialCode,
    },
  }

  const validationSchema = createInvitePlayerValidationSchema()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(validationSchema as any),
  })

  const toggleModal = () => setIsOpen(!isOpen)

  const onChangeCountry = (country: Country) => {
    setSelectedCountry(country)
    setValue('phoneData.isoCode', selectedCountry.isoCode)
    setValue('phoneData.dialCode', selectedCountry.dialCode)
  }

  const onChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[0-9]*$/
    if (regex.test(e.target.value)) {
      setValue('phoneData.phoneNumber', e.target.value)
    }
  }

  const onSubmit = handleSubmit(async (data) => {
    setInviting(true);
    const payload: PayloadClubInviteMember = {
      ...(clubId && {
        clubId: clubId,
      }),
      email: data.email,
      fullName: `${data?.firstName?.trim() || ''} ${data?.lastName?.trim() || ''}`,
      phone: data.phoneData.phoneNumber ? `${data.phoneData.dialCode}${data.phoneData.phoneNumber}` : '',
      isoCode: data.phoneData.isoCode,
    }
    if (clubId) {
      inviteClubMember(payload, {
        onSuccess: (res) => {
          toast.success(res.result || `${t('common.invitePlayerSuccess')}!`)
          setInviting(false);
          onClose()
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || APP_CUSTOM_ERROR)
          setInviting(false);
        },
      })
    } else {
      invitePlayer(payload, {
        onSuccess: () => {
          toast.success(`${t('common.invitePlayerSuccess')}!`)
          onClose()
          setInviting(false);
        },
        onError: () => {
          toast.error(APP_CUSTOM_ERROR)
          setInviting(false);
        },
      })
    }
  })

  useEffect(() => {
    setValue('phoneData.isoCode', selectedCountry.isoCode)
    setValue('phoneData.dialCode', selectedCountry.dialCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry])

  useEffect(() => {
    reset(defaultValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[1000]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col max-w-lg transform overflow-hidden rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={onClose}
                >
                  <Close className="text-navy-900" />
                </button>

                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('club.inviteAPLayer')}
                </Dialog.Title>

                <div className="flex flex-col gap-y-4 px-6 py-4">
                  <p className="text-gray-500 font-medium text-sm">{t('club.inviteAPLayerDesc')}</p>

                  <div className="flex flex-col gap-y-2">
                    <h5 className="text-navy-900 font-semibold text-sm">{t('common.firstName')}</h5>
                    <input
                      className={`w-full p-4 border rounded-full ${errors.firstName ? 'border-warn' : ''
                        } text-sm text-navyBlue focus:outline-none`}
                      type="text"
                      placeholder={t('common.firstNamePlaceHolder')}
                      {...register('firstName')}
                    />
                    {errors?.firstName && <p className={`text-xs text-warn`}>{errors?.firstName?.message}</p>}
                  </div>

                  <div className="flex flex-col gap-y-2">
                    <h5 className="text-navy-900 font-semibold text-sm">{t('common.lastName')}</h5>
                    <input
                      className={`w-full p-4 border rounded-full ${errors.lastName ? 'border-warn' : ''
                        } text-sm text-navyBlue focus:outline-none`}
                      type="text"
                      placeholder={t('common.lastNamePlaceHolder')}
                      {...register('lastName')}
                    />
                    {errors?.lastName && <p className={`text-xs text-warn`}>{errors?.lastName?.message}</p>}
                  </div>

                  <div className="flex flex-col gap-y-2">
                    <h5 className="text-navy-900 font-semibold text-sm">{t('common.email')}</h5>
                    <input
                      className={`w-full p-4 border rounded-full ${errors.email ? 'border-warn' : ''
                        } text-sm text-navyBlue focus:outline-none`}
                      type="text"
                      autoFocus
                      placeholder={t('common.emailPlaceHolder')}
                      {...register('email')}
                    />
                    {errors?.email && <p className={`text-xs text-warn`}>{errors?.email?.message}</p>}
                  </div>

                  <div className="flex flex-1 flex-col gap-y-2">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-navy-900">
                        {t('common.phone')}
                        <span className="text-xs text-gray-500 font-normal ml-2">{t('common.optional')}</span>
                      </span>
                      <p className="text-gray-500 text-sm">{t('common.messageAndDataRatesMayApply')}</p>
                    </div>
                    <Controller
                      name="phoneData.phoneNumber"
                      control={control}
                      render={() => (
                        <div className="flex gap-x-3 border border-navy-900 border-opacity-20 p-[7px] rounded-full">
                          <button
                            className="flex gap-x-1 bg-slate-100 rounded-full p-[3px] md:p-2"
                            onClick={toggleModal}
                          >
                            <p>{selectedCountry.emoji}</p>
                            <p>{selectedCountry.dialCode}</p>
                            <ArrowDropdown />
                          </button>
                          <input
                            className="text-sm w-full focus:outline-none text-navy-900"
                            type="text"
                            value={watch('phoneData.phoneNumber')}
                            placeholder={t('common.phonePlaceHolder')}
                            maxLength={15}
                            onChange={(e) => onChangePhoneNumber(e)}
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
                <div className="flex justify-center">
                  <ButtonPrimary
                    title={t('common.sendInvite')}
                    className={`!py-[6px] !px-4 !rounded`}
                    onClick={onSubmit}
                    disabled={inviting}
                  >
                    {inviting ? <LoadingInline /> : undefined}
                  </ButtonPrimary>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        <SelectCountryModal
          open={isOpen}
          onClose={() => setIsOpen(false)}
          onSubmit={onChangeCountry}
          isoCode={watch(`phoneData.isoCode`)}
          dialCode={watch(`phoneData.dialCode`)}
        />
      </Dialog>
    </Transition>
  )
}
